import { useQuery } from '@tanstack/react-query';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

export interface GetSellerRonSessionResponse {
	sessionStatus: 'not_started' | 'in_progress' | 'completed' | 'failed';
	whitelabelToken?: string;
}

/**
 * API function to get the current seller RON session status.
 */
export const getSellerRonSession = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetSellerRonSessionResponse> => {
	const response = await apiClient.get<GetSellerRonSessionResponse>(
		ordersEndpoints.sellerRonSession(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for fetching the seller RON session status.
 *
 * Returns whether the session is not_started, in_progress (with whitelabelToken),
 * completed, or failed. This lets the component decide whether to show the intro
 * page or immediately resume the NotaryLive iframe.
 *
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetSellerRonSession = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.sellerRonSession.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to get seller RON session without an order ID.');
			}
			return getSellerRonSession(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
